<script lang="ts">
	import { mainNavigation } from '$lib/content/data/navigation';
	import { createDrawerSwipeHandler } from '$lib/utils/touchGestures';
	import ThemeToggle from '$lib/components/ThemeToggle.svelte';
	import { onMount } from 'svelte';

	let { pathname = '/' } = $props();

	let mobileMenuOpen = $state(false);
	let scrollY = 0;

	function lockScroll() {
		if (typeof window === 'undefined') return;
		scrollY = window.scrollY;
		document.body.style.position = 'fixed';
		document.body.style.top = `-${scrollY}px`;
		document.body.style.width = '100%';
		document.body.style.overflow = 'hidden';
	}

	function unlockScroll() {
		if (typeof window === 'undefined') return;
		document.body.style.position = '';
		document.body.style.top = '';
		document.body.style.width = '';
		document.body.style.overflow = '';
		window.scrollTo(0, scrollY);
	}

	function toggleMobileMenu() {
		mobileMenuOpen = !mobileMenuOpen;
		if (mobileMenuOpen) {
			lockScroll();
		} else {
			unlockScroll();
		}
	}

	function closeMobileMenu() {
		mobileMenuOpen = false;
		unlockScroll();
	}

	function isActive(href: string): boolean {
		if (href === '/') {
			return pathname === '/';
		}
		return pathname.startsWith(href);
	}

	// Setup drawer swipe handler
	let drawerElement: HTMLElement;
	let swipeHandler: { destroy: () => void };

	onMount(() => {
		if (drawerElement) {
			swipeHandler = createDrawerSwipeHandler({
				element: drawerElement,
				threshold: 80,
				onClose: closeMobileMenu,
				onTransform: (transform) => {
					drawerElement.style.transition = transform ? 'none' : '';
					drawerElement.style.transform = transform || `translateX(${mobileMenuOpen ? '0' : '100%'})`;
				}
			});
		}

		return () => {
			swipeHandler?.destroy();
		};
	});
</script>

<svelte:window onkeydown={(e) => { if (e.key === 'Escape' && mobileMenuOpen) closeMobileMenu(); }} />

<header class="fixed top-0 left-0 right-0 z-50 bg-ak-deep/80 backdrop-blur-md border-b border-ak-border-subtle" style="padding-top: env(safe-area-inset-top, 0px);">
	<div class="container mx-auto px-4 h-16 flex items-center justify-between">
		<!-- Logo (mint on dark, green on light — swapped via CSS, no flash) -->
		<a href="/" class="flex items-center gap-2 transition-transform hover:scale-105 active:scale-95" aria-label="Akonia Codex - Home">
			<img alt="Akonia Codex" width="700" height="130" class="h-8 w-auto header-logo" fetchpriority="high" decoding="async" />
		</a>

		<!-- Right cluster: nav + theme toggle + CTA + hamburger -->
		<div class="flex items-center gap-3 md:gap-5">
			<!-- Desktop Navigation -->
			<nav class="hidden md:flex items-center gap-8" aria-label="Main navigation">
				{#each mainNavigation as item}
					<a
						href={item.href}
						class="text-sm text-ak-text-secondary hover:text-ak-wintergreen transition-colors {isActive(item.href) ? 'text-ak-wintergreen' : ''}"
						aria-current={isActive(item.href) ? 'page' : undefined}
					>
						{item.label}
					</a>
				{/each}
			</nav>

			<ThemeToggle />

			<!-- CTA Button (Desktop) -->
			<a
				href="/contact"
				class="hidden md:inline-flex items-center justify-center px-5 py-2.5 min-h-12 bg-ak-wintergreen text-ak-deep font-medium text-sm rounded-md hover:opacity-90 active:opacity-80 active:scale-[0.98] transition-all touch-manipulation"
			>
				Contact
			</a>

			<!-- Mobile Menu Toggle -->
			<button
				type="button"
				class="md:hidden flex items-center justify-center w-12 h-12 min-h-12 rounded-lg text-ak-text-primary hover:bg-ak-surface active:bg-ak-surface-overlay active:scale-[0.95] transition-colors will-change-colors will-change-transform touch-manipulation"
				onclick={toggleMobileMenu}
				aria-expanded={mobileMenuOpen}
				aria-controls="mobile-menu"
				aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
			>
				{#if mobileMenuOpen}
					<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
					</svg>
				{:else}
					<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
					</svg>
				{/if}
			</button>
		</div>
	</div>
</header>

	<!-- Mobile Menu Overlay -->
	{#if mobileMenuOpen}
		<button
			class="fixed inset-0 bg-ak-deep/80 z-40 md:hidden outline-none focus-visible:ring-2 focus-visible:ring-ak-wintergreen cursor-default"
			onclick={closeMobileMenu}
			aria-label="Close menu background"
		></button>
	{/if}

	<!-- Mobile Menu Drawer -->
	<div
		bind:this={drawerElement}
		id="mobile-menu"
		class="fixed right-0 top-16 bottom-0 w-80 max-w-[85vw] bg-ak-surface/98 backdrop-blur-2xl border-l border-ak-border-subtle z-50 md:hidden shadow-2xl shadow-black/80 transition-transform duration-300 ease-out flex flex-col justify-between"
		style="padding-bottom: env(safe-area-inset-bottom, 0px); transform: translateX({mobileMenuOpen ? '0' : '100%'}); touch-action: pan-y;"
		aria-hidden={!mobileMenuOpen}
	>
		<nav class="p-5 overflow-y-auto flex-1 flex flex-col justify-between" aria-label="Mobile navigation" style="-webkit-overflow-scrolling: touch;">
			<div>
				<div class="text-xs uppercase tracking-wider text-ak-text-muted font-semibold px-3 mb-3">Navigation</div>
				<ul class="space-y-1.5">
					{#each mainNavigation as item}
						{@const active = isActive(item.href)}
						<li>
							<a
								href={item.href}
								class="flex items-center justify-between px-3.5 py-3 min-h-12 rounded-xl text-sm font-medium transition-all touch-manipulation {active ? 'bg-ak-wintergreen/10 text-ak-wintergreen border-l-2 border-ak-wintergreen shadow-sm' : 'text-ak-text-secondary hover:text-ak-text-primary hover:bg-ak-surface-overlay active:bg-ak-surface-overlay active:scale-[0.98]'}"
								onclick={closeMobileMenu}
								aria-current={active ? 'page' : undefined}
							>
								<div class="flex flex-col">
									<span class="{active ? 'font-semibold text-ak-wintergreen' : 'text-ak-text-primary'}">{item.label}</span>
									{#if item.description}
										<span class="text-xs text-ak-text-muted line-clamp-1 mt-0.5">{item.description}</span>
									{/if}
								</div>
								{#if item.label === 'AI Lab'}
									<span class="text-xs uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-ak-wintergreen/20 text-ak-wintergreen shrink-0 ml-2">Free</span>
								{:else if item.label === 'Masterclass'}
									<span class="text-xs uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-ak-wintergreen text-ak-deep shrink-0 ml-2">Live</span>
								{/if}
							</a>
						</li>
					{/each}
				</ul>
			</div>

			<div class="mt-6 pt-5 border-t border-ak-border-subtle">
				<a
					href="/contact"
					class="flex items-center justify-center w-full px-4 py-3.5 min-h-12 bg-ak-wintergreen text-ak-deep font-semibold rounded-xl text-sm shadow-md hover:opacity-90 active:opacity-80 active:scale-[0.98] transition-all touch-manipulation"
					onclick={closeMobileMenu}
				>
					Contact Us
				</a>
				<div class="mt-4 text-center">
					<p class="text-xs text-ak-text-muted">Spec-first development & training</p>
				</div>
			</div>
		</nav>
	</div>

<!-- Spacer for fixed header -->
<div class="h-16"></div>
